import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

const SCOPES = [
  { value: '*', label: 'Full access (all scopes)' },
  { value: 'contacts:read', label: 'Contacts: Read' },
  { value: 'contacts:write', label: 'Contacts: Write' },
  { value: 'campaigns:read', label: 'Campaigns: Read' },
  { value: 'campaigns:write', label: 'Campaigns: Write' }
];

export default function WhatsAppApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: ['*'], rateLimit: 60 });
  const [newKey, setNewKey] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` };

  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/saas/api-keys`, { headers }); setKeys(data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return alert('Name required');
    try {
      const { data } = await axios.post(`${API}/api/saas/api-keys`, form, { headers });
      setNewKey(data);
      setShowCreate(false);
      setForm({ name: '', scopes: ['*'], rateLimit: 60 });
      load();
    } catch { alert('Failed'); }
  };

  const toggle = async (id, enabled) => {
    await axios.patch(`${API}/api/saas/api-keys/${id}`, { enabled: !enabled }, { headers });
    load();
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this key? API calls with it will immediately fail.')) return;
    await axios.delete(`${API}/api/saas/api-keys/${id}`, { headers });
    load();
  };

  const toggleScope = (s) => {
    const set = new Set(form.scopes);
    if (set.has(s)) set.delete(s); else set.add(s);
    setForm({ ...form, scopes: [...set] });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>🔑 API Keys</h2>
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>+ New API Key</button>
      </div>

      <div style={{ background: '#eff6ff', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
        💡 API base: <code>{API}/api/v1</code> · Auth header: <code>X-API-Key: oralzy_...</code>
        <br/>Endpoints: <code>/status</code>, <code>/contacts</code>, <code>/campaigns</code>
      </div>

      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              <th style={th}>Name</th>
              <th style={th}>Prefix</th>
              <th style={th}>Scopes</th>
              <th style={th}>Requests</th>
              <th style={th}>Last used</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={td}>{k.name}</td>
                <td style={td}><code style={{ fontSize: 12 }}>{k.prefix}…</code></td>
                <td style={td}>{k.scopes.join(', ')}</td>
                <td style={td}>{k.requestCount}</td>
                <td style={td}>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'never'}</td>
                <td style={td}>
                  <label style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={k.enabled} onChange={() => toggle(k._id, k.enabled)} /> {k.enabled ? 'Active' : 'Disabled'}
                  </label>
                </td>
                <td style={td}>
                  <button onClick={() => revoke(k._id)} style={{ ...miniBtn, color: '#ef4444' }}>Revoke</button>
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr><td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: '#888' }}>No API keys yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="New API Key">
          <input placeholder="Key name (e.g. Website integration)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={input} />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Scopes:</div>
          {SCOPES.map(s => (
            <label key={s.value} style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>
              <input type="checkbox" checked={form.scopes.includes(s.value)} onChange={() => toggleScope(s.value)} /> {s.label}
            </label>
          ))}
          <input type="number" placeholder="Rate limit (req/min)" value={form.rateLimit} onChange={e => setForm({ ...form, rateLimit: +e.target.value })} style={{ ...input, marginTop: 10 }} />
          <button onClick={create} style={btnPrimary}>Generate Key</button>
        </Modal>
      )}

      {newKey && (
        <Modal onClose={() => setNewKey(null)} title="🔑 API Key Generated">
          <div style={{ background: '#fef3c7', padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            ⚠️ Copy this key NOW. It will not be shown again.
          </div>
          <div style={{ background: '#1f2937', color: '#10b981', padding: 16, borderRadius: 6, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', marginBottom: 12 }}>
            {newKey.key}
          </div>
          <button onClick={() => { navigator.clipboard.writeText(newKey.key); alert('Copied!'); }} style={btnPrimary}>📋 Copy to clipboard</button>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const input = { padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 10, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const miniBtn = { padding: '4px 10px', fontSize: 12, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' };
const th = { padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#374151' };
const td = { padding: 12, fontSize: 14 };
